import {
  applyMatrixToPoint,
  createMatrixFromArray,
  drawLabel,
} from './floorPlanUtils';
import { getAllRoomPolygons, isPointInPolygon } from './roomPolygonUtils';

/**
 * Finds exterior doors in a floor plan (doors with a room on only one side)
 * @param {Object} data - The floor plan data
 * @param {Number} checkDistance - Distance to check for rooms on each side (in meters)
 * @param {Number} tolerance - Tolerance for point matching
 * @returns {Array} Array of exterior doors
 */
export const findExteriorDoors = (
  data,
  rotation,
  checkDistance = 0.5,
  tolerance = 0.1
) => {
  if (!data || !data.doors || !data.rooms) {
    return [];
  }

  // Get all room polygons for spatial testing
  const roomPolygons = getAllRoomPolygons(data, rotation);

  // Array to store exterior doors
  const exteriorDoors = [];

  // Check each door
  data.doors.forEach((door) => {
    // Get door transformation
    const matrix = createMatrixFromArray(door.transform);
    const dimensions = door.dimensions || [1, 2, 0];
    const doorWidth = dimensions[0];

    // Get door center in world space
    const center = applyMatrixToPoint(matrix, [0, 0, 0]);

    // Get door direction from matrix
    const direction = [matrix[0], matrix[2]]; // X and Z components of first column
    const length = Math.sqrt(
      direction[0] * direction[0] + direction[1] * direction[1]
    );
    const normalizedDir = [direction[0] / length, direction[1] / length];

    // Get perpendicular direction (for checking on either side of the door)
    const perpDir = [-normalizedDir[1], normalizedDir[0]];

    // Check points on either side of the door
    const sidePoint1 = [
      center[0] + perpDir[0] * checkDistance,
      center[2] + perpDir[1] * checkDistance,
    ];

    //double check
    const doubleCheckPoint1 = [
      center[0] + perpDir[0] * checkDistance * 3,
      center[2] + perpDir[1] * checkDistance * 3,
    ];

    const sidePoint2 = [
      center[0] - perpDir[0] * checkDistance,
      center[2] - perpDir[1] * checkDistance,
    ];

    const doubleCheckPoint2 = [
      center[0] - perpDir[0] * checkDistance * 3,
      center[2] - perpDir[1] * checkDistance * 3,
    ];

    // Check if each point is inside a room
    let side1HasRoom = false;
    let side2HasRoom = false;
    let check1HasRoom = false;
    let check2HasRoom = false;

    // Check each room polygon
    Object.values(roomPolygons).forEach((polygon) => {
      if (polygon && polygon.points && polygon.points.length >= 3) {
        // Check if side point 1 is in this room
        if (isPointInPolygon(sidePoint1, polygon.points)) {
          side1HasRoom = true;
        }

        // Check if side point 2 is in this room
        if (isPointInPolygon(sidePoint2, polygon.points)) {
          side2HasRoom = true;
        }

        // Check check points
        if (isPointInPolygon(doubleCheckPoint1, polygon.points)) {
          check1HasRoom = true;
        }

        if (isPointInPolygon(doubleCheckPoint2, polygon.points)) {
          check2HasRoom = true;
        }
      }
    });

    // If only one side has a room, it's an exterior door
    if (
      (side1HasRoom && !side2HasRoom && !check2HasRoom) ||
      (!side1HasRoom && side2HasRoom && !check1HasRoom)
    ) {
      // Determine which side is the interior side
      const interiorSide = side1HasRoom ? 'side1' : 'side2';

      // Mark this door as exterior and note which side is interior
      exteriorDoors.push({
        door,
        isExterior: true,
        interiorSide,
        center: [center[0], center[1], center[2]],
        direction: normalizedDir,
        perpDirection: perpDir,
        interiorPoint: interiorSide === 'side1' ? sidePoint1 : sidePoint2,
        exteriorPoint: interiorSide === 'side1' ? sidePoint2 : sidePoint1,
      });
    }
  });

  return exteriorDoors;
};

/**
 * Draws exterior doors with special highlighting
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} exteriorDoors - Array of exterior doors from findExteriorDoors
 * @param {Object} options - Display options
 */
export const drawExteriorDoors = (ctx, exteriorDoors, options = {}) => {
  if (!exteriorDoors || exteriorDoors.length === 0) return;

  const showLabels = options.showLabels !== false;
  const highlightColor = options.highlightColor || '#ff3300';
  const selectedId = options.selectedId || null;

  exteriorDoors.forEach((extDoor) => {
    const {
      door,
      center,
      direction,
      perpDirection,
      interiorPoint,
      exteriorPoint,
    } = extDoor;

    // Check if this door is selected
    const isSelected = selectedId && door.id === selectedId;
    const color = isSelected ? '#ff9900' : highlightColor;

    // Draw door highlight
    const scale = ctx.getTransform().a;

    // Draw door center marker
    ctx.beginPath();
    ctx.arc(center[0], center[2], 0.3, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();

    // Draw line indicating exterior direction
    ctx.beginPath();
    ctx.moveTo(center[0], center[2]);
    ctx.lineTo(exteriorPoint[0], exteriorPoint[1]);
    ctx.strokeStyle = color;
    ctx.lineWidth = 0.1;
    ctx.setLineDash([0.2, 0.1]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw exterior marker (arrow or exit sign)
    ctx.beginPath();
    ctx.moveTo(
      exteriorPoint[0] - perpDirection[0] * 0.3,
      exteriorPoint[1] - perpDirection[1] * 0.3
    );
    ctx.lineTo(exteriorPoint[0], exteriorPoint[1]);
    ctx.lineTo(
      exteriorPoint[0] + perpDirection[0] * 0.3,
      exteriorPoint[1] + perpDirection[1] * 0.3
    );
    ctx.strokeStyle = color;
    ctx.lineWidth = 0.15;
    ctx.stroke();

    // Draw exit label if enabled
    if (showLabels) {
      drawLabel(
        ctx,
        'EXIT',
        center[0] + direction[0] * 0.5,
        center[2] + direction[1] * 0.5,
        {
          fontSize: 10,
          color: '#ffffff',
          backgroundColor: color,
          padding: 2,
        }
      );
    }
  });
};

/**
 * Gets the closest exterior door to a given point
 * @param {Array} point - World coordinates [x, y, z]
 * @param {Array} exteriorDoors - Array of exterior doors
 * @returns {Object} The closest exterior door or null if none found
 */
export const getClosestExteriorDoor = (point, exteriorDoors) => {
  if (!exteriorDoors || exteriorDoors.length === 0) return null;

  let closestDoor = null;
  let minDistance = Infinity;

  exteriorDoors.forEach((extDoor) => {
    const { center } = extDoor;
    const dx = point[0] - center[0];
    const dz = point[2] - center[2];
    const distance = Math.sqrt(dx * dx + dz * dz);

    if (distance < minDistance) {
      minDistance = distance;
      closestDoor = extDoor;
    }
  });

  return closestDoor;
};
